import {QuerySampleGroup} from './types';

export const QUERY_SAMPLE_GROUPS: QuerySampleGroup[] = [
    {
        id: 'keywords',
        title: 'Keyword combinations',
        items: [
            {
                id: 'implicit-or',
                title: 'Default OR between words',
                query: 'refund delayed escalated',
                description: 'Without AND, any of the words can match.',
            },
            {
                id: 'multi-and',
                title: 'All keywords',
                query: 'refund AND delayed AND escalated',
                description: 'Finds texts that contain all of the keywords.',
            },
            {
                id: 'required-plus',
                title: 'Required term with +',
                query: '+refund delayed escalated',
                description: 'Refund is required; the other terms are optional.',
            },
            {
                id: 'multi-exclude',
                title: 'Keywords with exclusion',
                query: 'refund AND delayed AND escalated -spam -test',
                description: 'Finds texts with all required keywords and without the negated terms.',
            },
            {
                id: 'not-operator',
                title: 'Exclusion with NOT',
                query: 'refund NOT spam',
                description: 'Same intent as -spam but using the NOT operator.',
            },
            {
                id: 'grouped-not',
                title: 'Grouped NOT',
                query: 'refund AND NOT (spam OR test)',
                description: 'Excludes texts matching either spam or test.',
            },
            {
                id: 'or-search',
                title: 'Either keyword',
                query: 'refund OR reimbursement',
                description: 'Finds texts if either keyword is present.',
            },
        ],
    },
    {
        id: 'fields',
        title: 'Field-targeted search',
        items: [
            {
                id: 'field-text',
                title: 'Search event text',
                query: 'event_text:refund',
                description: 'Keyword search limited to the main text field.',
            },
            {
                id: 'field-agent-phrase',
                title: 'Agent phrase',
                query: 'event_agent_text:"thank you for calling"',
                description: 'Exact phrase in agent text only.',
            },
            {
                id: 'field-customer-or',
                title: 'Customer emotion keywords',
                query: 'event_customer_text:(angry OR frustrated OR upset)',
                description: 'OR group scoped to customer text.',
            },
            {
                id: 'field-mixed',
                title: 'Different fields combined',
                query: 'event_text:cancel* AND event_agent_text:policy',
                description: 'Wildcard in one field AND keyword in another.',
            },
            {
                id: 'field-bot-system',
                title: 'Bot and system messages',
                query: 'event_bot_text:handoff OR event_system_text:timeout',
                description: 'Search across chat-specific fields.',
            },
        ],
    },
    {
        id: 'partial',
        title: 'Partial matching',
        items: [
            {
                id: 'prefix',
                title: 'Prefix wildcard',
                query: 'cancel*',
                description: 'Finds strings starting with cancel.',
                matches: ['cancel', 'cancels', 'canceled', 'cancellation'],
            },
            {
                id: 'suffix',
                title: 'Suffix wildcard',
                query: '*ment',
                description: 'Finds strings ending with ment.',
                matches: ['reimbursement', 'management'],
            },
            {
                id: 'infix',
                title: 'Infix wildcard',
                query: 'ref*nd',
                description: 'Finds strings with unknown characters in the middle.',
                matches: ['refund', 'refnd'],
            },
            {
                id: 'single-char',
                title: 'Single unknown character',
                query: 'ch?t',
                description: 'Finds words with one unknown letter.',
                matches: ['chat', 'chit'],
            },
            {
                id: 'multi-char',
                title: 'Multiple unknown characters',
                query: 'c??cel',
                description: 'Each ? matches exactly one character.',
                matches: ['cancel', 'cancer'],
            },
            {
                id: 'wildcard-and-keyword',
                title: 'Wildcard with keyword',
                query: 'cancel* AND policy',
                description: 'Combines prefix wildcard with a required keyword.',
            },
        ],
    },
    {
        id: 'phrase',
        title: 'Phrase and proximity',
        items: [
            {
                id: 'phrase-exact',
                title: 'Exact phrase',
                query: '"not satisfied"',
                description: 'Finds the exact phrase between quotation marks.',
            },
            {
                id: 'multi-phrase-and',
                title: 'Multiple phrases (AND)',
                query: '"not satisfied" AND "speak to manager"',
                description: 'Both phrases must appear in the same text.',
            },
            {
                id: 'multi-phrase-or',
                title: 'Multiple phrases (OR)',
                query: '"thank you for calling" OR "have a nice day"',
                description: 'Either closing phrase is enough.',
            },
            {
                id: 'phrase-proximity-1',
                title: 'Proximity with small deviation',
                query: '"not satisfied"~1',
                description: 'Allows one extra word inside the phrase.',
                matches: ['not really satisfied', 'not very satisfied', 'not satisfied'],
            },
            {
                id: 'phrase-proximity-zero',
                title: 'Zero slop proximity',
                query: '"not satisfied"~0',
                description: 'Equivalent to an exact phrase match.',
            },
            {
                id: 'phrase-proximity-10',
                title: 'Proximity with larger deviation',
                query: '"agent was rude"~10',
                description: 'Allows up to ten extra words between phrase parts.',
                matches: ['the agent honestly was, quite frankly, rude'],
            },
            {
                id: 'phrase-proximity-2-fail',
                title: 'Proximity too strict',
                query: '"agent was rude"~2',
                description: 'Would not match the longer example above because the deviation is too small.',
            },
        ],
    },
    {
        id: 'fuzzy',
        title: 'Fuzzy matching',
        items: [
            {
                id: 'fuzzy-default',
                title: 'Default fuzzy distance',
                query: 'refund~',
                description: 'Uses the default allowed edit distance for typos.',
            },
            {
                id: 'fuzzy-cancelation',
                title: 'Typo tolerance',
                query: 'cancelation~2',
                description: 'Finds misspellings and typos.',
                matches: ['cancelation', 'cancellation'],
            },
            {
                id: 'fuzzy-refund-1',
                title: 'Low fuzzy deviation',
                query: 'refund~1',
                description: 'Matches refnd but not refnds.',
                matches: ['refnd'],
            },
            {
                id: 'fuzzy-refund-2',
                title: 'Higher fuzzy deviation',
                query: 'refund~2',
                description: 'Matches both refnd and refnds.',
                matches: ['refnd', 'refnds'],
            },
        ],
    },
    {
        id: 'special-terms',
        title: 'Hyphenated and special terms',
        items: [
            {
                id: 'hyphenated-phrase',
                title: 'Hyphenated phrase',
                query: '"co-pay"',
                description: 'Use quotes when searching for hyphenated expressions.',
            },
            {
                id: 'escaped-hyphen',
                title: 'Escaped hyphen in term',
                query: 'e\\-mail',
                description: 'Escape special characters when searching for them literally.',
            },
            {
                id: 'escaped-question',
                title: 'Literal question mark',
                query: 'status\\?',
                description: 'Prevents ? from being treated as a single-character wildcard.',
            },
        ],
    },
    {
        id: 'datetime',
        title: 'Date range',
        items: [
            {
                id: 'event-datetime-closed',
                title: 'Closed time range',
                query: 'event_datetime:[2026-06-01T00:00:00 TO 2026-06-30T23:59:59]',
                description: 'Finds events within an inclusive date range.',
            },
            {
                id: 'event-datetime-open-end',
                title: 'From date onward',
                query: 'event_datetime:[2026-06-01T00:00:00 TO *]',
                description: 'Everything on or after the start date.',
            },
            {
                id: 'event-datetime-open-start',
                title: 'Up to date',
                query: 'event_datetime:[* TO 2026-06-30T23:59:59]',
                description: 'Everything on or before the end date.',
            },
            {
                id: 'event-datetime-exclusive',
                title: 'Exclusive date range',
                query: 'event_datetime:{2026-06-01T00:00:00 TO 2026-06-30T23:59:59}',
                description: 'Excludes the boundary dates themselves.',
            },
        ],
    },
];
